import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { SpeseService, IncaricoService, RelazioneGiornalieraService, ServizioFiduciarioService, SettingsService } from '../services/apiService';
import { Incarico } from '../types';
import {
    Calendar, Receipt, Edit2, CheckCircle, Clock,
    AlertTriangle, ChevronLeft, ChevronRight, Save,
    Camera, Car, Loader2, X, Plus, Send, Folder,
    Briefcase, Shield, FileText, RotateCcw, Activity,
} from 'lucide-react';
import { useToast } from "../components/ToastContext";
import { Modal } from "../components/Modal";
import { parseDecimal, formatOreInHM, localIsoDate } from "../utils/utils";
import { filterIncarichiMese } from "../utils/speseCollaboratoreFilters";
import NumberInput from '../components/NumberInput';
import AutoAziendaleSelector from '../components/AutoAziendaleSelector';
import { generaRiepilogoCollab } from '../services/generaRiepilogoCollaboratore';

// Le spese arrivano da due tabelle con numerazioni proprie (dentro un report / fuori dal
// report): l'id da solo può ripetersi nella stessa lista, la chiave di riga distingue le due.
const chiaveSpesa = (s: any): string => `${s?.relazioneId ? 'A' : 'B'}-${s?.id}`;

const TIPI_SPESA = ['Pasti', 'Trasporti', 'Carburante', 'Alloggio', 'Altro'];
const isKmType = (tipo: string) => tipo?.toLowerCase() === 'km';

const STATUS_CFG: Record<string, { label: string; icon: React.ReactNode; row: string; badge: string }> = {
    APPROVATO: { label: 'Approvato', icon: <CheckCircle size={12} />, row: 'border-emerald-200 bg-emerald-50/40', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    RIFIUTATO: { label: 'Rifiutato', icon: <AlertTriangle size={12} />, row: 'border-rose-200 bg-rose-50/40', badge: 'bg-rose-100 text-rose-700 border-rose-200' },
    MODIFICATO: { label: 'Modificato', icon: <Edit2 size={12} />, row: 'border-amber-200 bg-amber-50/40', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
    IN_ATTESA: { label: 'In Attesa', icon: <Clock size={12} />, row: 'border-blue-100 bg-blue-50/30', badge: 'bg-blue-100 text-blue-700 border-blue-200' },
};
const getStatus = (s: string) => STATUS_CFG[s] ?? STATUS_CFG['IN_ATTESA'];

interface SpesaRow { data: string; tipo: string; importo: string; kmPercorsi: string; note: string; anticipatoAzienda: boolean; autoAziendale: boolean; file: File | null; }
const newRow = (defaultDate: string): SpesaRow => ({ data: defaultDate, tipo: '', importo: '', kmPercorsi: '', note: '', anticipatoAzienda: false, autoAziendale: false, file: null });

const fmtOraHM = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '—';

// Timbrature start/stop di un lavoro: WorkSession degli incarichi (startTime/endTime)
// o TurnoSessioneDto dei turni (startAt/endAt). Restituisce "08:12–14:03 · 17:30–20:10",
// "…" per una sessione aperta, "—" se non ce ne sono. `filterDay` (YYYY-MM-DD) limita
// alle sessioni iniziate quel giorno; serve per gli incarichi multi-giorno.
const formatTimbrature = (sessioni: any[] | undefined | null, filterDay?: string | null): string => {
    if (!Array.isArray(sessioni) || sessioni.length === 0) return '—';
    let norm = sessioni
        .map(s => ({ start: s.startTime ?? s.startAt ?? null, end: s.endTime ?? s.endAt ?? null }))
        .filter(s => s.start);
    if (filterDay) {
        const day = String(filterDay).substring(0, 10);
        norm = norm.filter(s => String(s.start).substring(0, 10) === day);
    }
    if (norm.length === 0) return '—';
    norm.sort((a, b) => String(a.start).localeCompare(String(b.start)));
    return norm.map(s => `${fmtOraHM(s.start)}–${s.end ? fmtOraHM(s.end) : '…'}`).join(' · ');
};

// Data univoca per una nuova spesa quando non c'è ambiguità: data del report
// selezionato, oppure del lavoro se dura un solo giorno. Altrimenti null e la
// data la sceglie l'utente.
const resolveContextDate = (inc: any, turno: any, rel: any): string | null => {
    if (rel?.dataRelazione) return String(rel.dataRelazione).substring(0, 10);
    const ctx = inc || turno;
    if (!ctx) return null;
    const start = String(ctx.dataInizio || ctx.effettivoInizio || ctx.dataPianificazione || '').substring(0, 10);
    const endRaw = String(ctx.dataFine || ctx.effettivoFine || '').substring(0, 10);
    if (!start) return null;
    if (!endRaw || endRaw === start) return start;
    return null;
};

const Toggle: React.FC<{ checked: boolean; onChange: () => void; label: string; sub: string; activeClass: string; icon: React.ReactNode }> = ({ checked, onChange, label, sub, activeClass, icon }) => (
    <div onClick={onChange} className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between select-none ${checked ? activeClass : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`}>
        <div className="flex items-center gap-2.5">
            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${checked ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`}>
                {checked && <svg viewBox="0 0 12 12" className="w-2.5 h-2.5" fill="none" stroke="white" strokeWidth="2.5"><polyline points="2,6 5,9 10,3" /></svg>}
            </div>
            <div>
                <p className="text-[10px] font-black text-slate-700 uppercase tracking-tight">{label}</p>
                <p className="text-[9px] text-slate-400">{sub}</p>
            </div>
        </div>
        <div className="opacity-50">{icon}</div>
    </div>
);

const fmt = (d: string) => new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });

// Ore "Xh Ym" arrotondate a 10 minuti, come formatOreInHM in utils.ts e il PDF agenda.
const fmtHM = formatOreInHM;

// Etichetta mandato: "<RIF mandato> - <soggetto assegnato al collaboratore>", come
// in MieiIncarichiView/AgendaListView. Il collaboratore non vede il nome del cliente.
const praticaName = (inc: Incarico) => {
    const raw = (inc.pratica as any)?.name || (inc.pratica as any)?.nome || '';
    const prefix = raw.includes(' - ') ? raw.split(' - ')[0] : raw;
    const soggetto = (inc as any).soggettoNome || (() => {
        try {
            const r = (inc.pratica as any)?.datiAggiuntiviJson;
            const d = typeof r === 'string' ? JSON.parse(r) : r || {};
            return d.soggetti?.[0]?.nome || d.soggetto_nome || d.soggetto_denominazione || '';
        } catch { return ''; }
    })();
    if (prefix && soggetto) return `${prefix} - ${soggetto}`;
    return prefix || soggetto || `Incarico #${inc.id}`;
};

export const SpeseCollaboratoreView: React.FC = () => {
    const { user } = useAuth();
    const { addToast } = useToast();

    const [spese, setSpese] = useState<any[]>([]);
    const [relazioni, setRelazioni] = useState<any[]>([]);
    const [incarichi, setIncarichi] = useState<Incarico[]>([]);
    // Turni fiduciari del collaboratore, per le spese standalone collegate a un turno.
    const [turni, setTurni] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [mese, setMese] = useState(new Date().getMonth() + 1);
    const [anno, setAnno] = useState(new Date().getFullYear());
    // Loading separato per il PDF di riepilogo, distinto dal pulsante "Aggiorna".
    const [pdfLoading, setPdfLoading] = useState(false);
    // Ore fiduciarie del mese (turni chiusi) da /api/servizi/ore-fiduciarie, tenute
    // separate da quelle investigative.
    const [oreFid, setOreFid] = useState<{ oreTotali: number; importoTotale: number }>({
        oreTotali: 0, importoTotale: 0,
    });
    // settings.buoniPastoVisibiliCollaboratori: se il collaboratore vede le righe
    // buonoPastoAuto generate in approvazione. Default false.
    const [buoniPastoVisibili, setBuoniPastoVisibili] = useState(false);

    // Modale rettifica spesa
    const [editSpesa, setEditSpesa] = useState<any>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Rettifica del report giornaliero: il collaboratore vede orari, ore, km e auto
    // inviati (non il testo, che gestisce il titolare) e può correggerli rimandandoli
    // in approvazione tramite /modifica.
    const [editReport, setEditReport] = useState<any>(null);
    const [reportForm, setReportForm] = useState({
        orarioInizio: '', orarioFine: '', oreExtra: '', km: '', autoAziendale: false,
    });
    const [isSavingReport, setIsSavingReport] = useState(false);
    // Spese aggiunte durante la modifica del report.
    const [nuoveSpeseEdit, setNuoveSpeseEdit] = useState<SpesaRow[]>([]);

    const openEditReport = (rel: any) => {
        setReportForm({
            orarioInizio: rel.orarioInizioIndagine || '',
            orarioFine: rel.orarioFineIndagine || '',
            oreExtra: rel.oreExtra != null ? String(rel.oreExtra) : '',
            km: rel.kmPercorsi != null ? String(rel.kmPercorsi) : '',
            autoAziendale: !!rel.autoAziendale,
        });
        setNuoveSpeseEdit([]);
        setEditReport(rel);
    };

    // Elimina subito una spesa del report; il backend riporta la relazione a
    // DA_REVISIONARE (vedi SpeseService.eliminaSpesa).
    const handleDeleteSpesaFromReport = async (spesa: any) => {
        if (!editReport?.id) return;
        if (!window.confirm(`Eliminare "${spesa.tipo}" (€ ${(spesa.importoInviato || 0).toFixed(2)}) dal report?`)) return;
        try {
            await SpeseService.delete(spesa.id, spesa.relazioneId ?? editReport.id);
            addToast('Spesa eliminata', 'success');
            await loadData();
        } catch {
            addToast("Errore durante l'eliminazione della spesa", 'error');
        }
    };

    const addRigaEdit = () => {
        const defaultDate = editReport?.dataRelazione
            ? editReport.dataRelazione.substring(0, 10)
            : today;
        setNuoveSpeseEdit(prev => [...prev, newRow(defaultDate)]);
    };
    const removeRigaEdit = (i: number) =>
        setNuoveSpeseEdit(prev => prev.filter((_, idx) => idx !== i));
    const updateRigaEdit = (i: number, f: keyof SpesaRow, v: any) =>
        setNuoveSpeseEdit(prev => { const n = [...prev]; (n[i] as any)[f] = v; return n; });

    const handleSubmitEditReport = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editReport?.id) return;

        // Validazione nuove spese.
        for (let i = 0; i < nuoveSpeseEdit.length; i++) {
            const r = nuoveSpeseEdit[i];
            if (!r.tipo) { addToast(`Nuova spesa #${i + 1}: seleziona la categoria`, 'error'); return; }
            if (parseDecimal(r.importo) <= 0) { addToast(`Nuova spesa #${i + 1}: importo non valido`, 'error'); return; }
        }

        setIsSavingReport(true);
        try {
            // Crea le spese aggiuntive (con relazioneId → SpesaDettaglio).
            const parentIds = {
                incaricoId: editReport.incaricoId ?? null,
                turnoId: editReport.turnoId ?? null,
                relazioneId: editReport.id,
            };
            for (const r of nuoveSpeseEdit) {
                const fd = new FormData();
                fd.append('data', JSON.stringify({
                    data: r.data,
                    importoInviato: parseDecimal(r.importo),
                    tipo: r.tipo,
                    note: r.note,
                    anticipatoAzienda: r.anticipatoAzienda,
                    autoAziendale: r.autoAziendale,
                    km: r.kmPercorsi ? parseDecimal(r.kmPercorsi) : null,
                    ...parentIds,
                }));
                if (r.file) fd.append('file', r.file);
                await SpeseService.create(fd);
            }

            // Aggiorna orari/ore/km/auto del report; modifica() riporta sempre lo stato a DA_REVISIONARE.
            const payload: any = {
                orarioInizioIndagine: reportForm.orarioInizio || undefined,
                orarioFineIndagine: reportForm.orarioFine || undefined,
                autoAziendale: reportForm.autoAziendale,
            };
            if (reportForm.oreExtra !== '') payload.oreExtra = parseDecimal(reportForm.oreExtra);
            if (reportForm.km !== '')        payload.kmPercorsi = parseDecimal(reportForm.km);
            await RelazioneGiornalieraService.modifica(editReport.id, payload);

            const addedMsg = nuoveSpeseEdit.length > 0
                ? ` · ${nuoveSpeseEdit.length} spesa/e aggiunta/e`
                : '';
            addToast(`Report rettificato e re-inviato in approvazione${addedMsg}`, 'success');
            setEditReport(null);
            setNuoveSpeseEdit([]);
            loadData();
        } catch (e: any) {
            // Il BE spiega perché la spesa non è accettata (incarico non tuo o non più attivo).
            addToast(e?.response?.data?.message || 'Errore durante la rettifica del report', 'error');
        } finally {
            setIsSavingReport(false);
        }
    };

    // Modale nuova spesa
    const [nuovaIncarico, setNuovaIncarico] = useState<Incarico | null>(null);
    // Lo stesso modale vale per i turni: se valorizzato, il submit manda turnoId
    // invece di incaricoId (mai entrambi).
    const [nuovaTurno, setNuovaTurno] = useState<any | null>(null);
    // Se valorizzato, le nuove spese si collegano a quella relazione (SpesaDettaglio
    // invece di SpesaCollaboratore) e il backend la riporta a DA_REVISIONARE.
    const [nuovaRelazione, setNuovaRelazione] = useState<any | null>(null);
    const [righe, setRighe] = useState<SpesaRow[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modalKm, setModalKm] = useState('');
    const [modalKmImporto, setModalKmImporto] = useState('');
    const [modalKmAuto, setModalKmAuto] = useState(false);
    const [modalKmData, setModalKmData] = useState('');

    const today = localIsoDate();

    useEffect(() => { if (user) loadData(); }, [user, mese, anno]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Ore fiduciarie del mese e turni del collaboratore per le spese standalone.
            const [incRes, speseRes, relRes, oreFidRes, turniRes, settingsRes] = await Promise.all([
                IncaricoService.getMyIncarichi(),
                SpeseService.getByCollaboratore(user!.id, mese, anno),
                RelazioneGiornalieraService.getMie(),
                ServizioFiduciarioService.getOreFiduciarie(user!.id, anno, mese)
                    .catch(() => ({ data: { oreTotali: 0, importoTotale: 0 } })),
                ServizioFiduciarioService.getMyTurni().catch(() => ({ data: [] })),
                SettingsService.getAll().catch(() => null),
            ]);
            setIncarichi(Array.isArray(incRes) ? incRes : (incRes as any)?.data ?? []);
            // Se il flag è false nascondiamo i buoni pasto generati dal sistema; il backend
            // li restituisce comunque.
            const settingsList: any[] = (settingsRes as any)?.data || (Array.isArray(settingsRes) ? settingsRes : []);
            const settings = Array.isArray(settingsList) && settingsList.length > 0 ? settingsList[0] : null;
            const visibili = !!settings?.buoniPastoVisibiliCollaboratori;
            setBuoniPastoVisibili(visibili);
            const speseRaw: any[] = (speseRes as any).data || [];
            setSpese(visibili ? speseRaw : speseRaw.filter((s: any) => !s?.buonoPastoAuto));
            setRelazioni(Array.isArray(relRes) ? relRes : []);
            const fid = (oreFidRes as any).data || { oreTotali: 0, importoTotale: 0 };
            setOreFid({
                oreTotali: Number(fid.oreTotali) || 0,
                importoTotale: Number(fid.importoTotale) || 0,
            });
            const turniList = (turniRes as any)?.data?.data || (turniRes as any)?.data || [];
            setTurni(Array.isArray(turniList) ? turniList : []);
        } catch { addToast('Errore nel caricamento dati', 'error'); }
        finally { setLoading(false); }
    };

    // Riepilogo mensile in PDF (stesso template di AgendaListView): relazioni
    // approvate e turni del mese. Avvisa solo se sono vuoti entrambi.
    const handleDownloadRiepilogo = () => {
        if (!user) return;
        setPdfLoading(true);
        try {
            const monthStr = `${anno}-${String(mese).padStart(2, '0')}`;
            const monthRelazioni = relazioni.filter((r: any) =>
                r.stato === 'APPROVATO'
                && typeof r.dataRelazione === 'string'
                && r.dataRelazione.startsWith(monthStr)
            );
            const monthTurni = turni.filter((t: any) =>
                typeof t.dataTurno === 'string' && t.dataTurno.startsWith(monthStr)
            );
            if (monthRelazioni.length === 0 && monthTurni.length === 0) {
                addToast('Nessun report approvato o turno nel mese selezionato', 'info');
                return;
            }
            const nomeCompleto = `${user.name || ''} ${user.surname || ''}`.trim() || 'Collaboratore';
            generaRiepilogoCollab(monthRelazioni, monthTurni, monthStr, nomeCompleto);
        } catch {
            addToast('Errore nella generazione del riepilogo', 'error');
        } finally {
            setPdfLoading(false);
        }
    };

    // Solo incarichi con attività nel mese (report o spese); logica in
    // utils/speseCollaboratoreFilters.ts.
    const incarichiMese = useMemo(
        () => filterIncarichiMese(incarichi, spese, relazioni, mese, anno),
        [incarichi, spese, relazioni, mese, anno],
    );

    const spesePerIncarico = useMemo(() => {
        const map: Record<string | number, any[]> = {};
        spese.forEach(s => { const k = s.incaricoId ?? 'none'; map[k] = [...(map[k] || []), s]; });
        return map;
    }, [spese]);

    // Spese fiduciarie raggruppate per turnoId.
    const spesePerTurno = useMemo(() => {
        const map: Record<string | number, any[]> = {};
        spese.forEach(s => {
            if (s.turnoId != null) {
                const k = s.turnoId;
                map[k] = [...(map[k] || []), s];
            }
        });
        return map;
    }, [spese]);

    // Il turno compare in "Le Mie Spese e KM" solo se il collaboratore ha già inviato
    // almeno una relazione per quel turno nel mese selezionato.
    const turniMese = useMemo(() => {
        return turni.filter((t: any) =>
            relazioni.some(r => {
                if (r.turnoId !== t.id) return false;
                if (!r.dataRelazione) return false;
                const d = new Date(r.dataRelazione);
                return d.getMonth() + 1 === mese && d.getFullYear() === anno;
            })
        );
    }, [turni, relazioni, mese, anno]);

    const relazioniPerIncarico = useMemo(() => {
        const map: Record<string | number, any[]> = {};
        relazioni.forEach(r => { const k = r.incaricoId ?? 'none'; map[k] = [...(map[k] || []), r]; });
        return map;
    }, [relazioni]);

    // Relazioni del mese per turno: la card del turno funziona come quella
    // dell'incarico (ReportInviatoCard, spese del report, "Modifica").
    const relazioniPerTurno = useMemo(() => {
        const map: Record<string | number, any[]> = {};
        relazioni.forEach(r => {
            if (r.turnoId != null) {
                const k = r.turnoId;
                map[k] = [...(map[k] || []), r];
            }
        });
        return map;
    }, [relazioni]);

    // Relazioni DA_MODIFICARE per incarico (badge "report da rifare").
    const daModificarePerIncarico = useMemo(() => {
        const map: Record<string | number, any[]> = {};
        relazioni
            .filter(r => r.stato === 'DA_MODIFICARE' && r.incaricoId != null)
            .forEach(r => { const k = r.incaricoId; map[k] = [...(map[k] || []), r]; });
        return map;
    }, [relazioni]);

    // Orfane: spese senza incaricoId né turnoId.
    const speseOrfane = useMemo(() =>
        spese.filter(s => s.incaricoId == null && s.turnoId == null),
        [spese]);

    // Spese il cui incarico o turno non è più caricato (es. mandato eliminato):
    // le mostriamo in sola lettura perché contano nei totali, ma non si possono
    // modificare senza il riferimento.
    const knownIncaricoIds = useMemo(
        () => new Set(incarichi.map((i: any) => i.id)),
        [incarichi],
    );
    const knownTurnoIds = useMemo(
        () => new Set(turni.map((t: any) => t.id)),
        [turni],
    );
    const speseDaParentEliminato = useMemo(() =>
        spese.filter(s =>
            // Flag del BE (pratica/servizio eliminati): modifica ed eliminazione sono già
            // bloccate lato server.
            s.parentEliminato ||
            // Fallback: il riferimento non è tra gli incarichi/turni del mese (copre anche
            // le cancellazioni definitive, dove il flag non arriva).
            (s.incaricoId != null && !knownIncaricoIds.has(s.incaricoId)) ||
            (s.turnoId != null && !knownTurnoIds.has(s.turnoId))
        ),
        [spese, knownIncaricoIds, knownTurnoIds]);

    // Ore del mese in 4 KPI: inserite dal collaboratore (registrate + extra dichiarate)
    // e approvate dall'azienda, separate tra investigazioni e servizi fiduciari
    // (mai sommate).
    const relazioniDelMese = useMemo(() => relazioni.filter(r => {
        if (!r.dataRelazione) return false;
        const d = new Date(r.dataRelazione);
        return d.getMonth() + 1 === mese && d.getFullYear() === anno;
    }), [relazioni, mese, anno]);

    // Investigazioni inserite: tutte le relazioni del mese, qualunque stato,
    // registrate + extra dichiarate.
    const oreInvestigativeInserite = useMemo(() => relazioniDelMese
        .filter(r => r.incaricoId)
        .reduce((sum, r) => sum + (r.oreGps || 0) + (r.oreExtra || 0), 0),
    [relazioniDelMese]);

    // Investigazioni approvate: solo relazioni APPROVATO. Prima oreApprovateTotali
    // (override del titolare), altrimenti GPS + extra approvate.
    // Non usiamo oreFatturabiliApprovate: sono le ore verso il cliente e possono
    // includere minimi contrattuali. Stessa regola del buono pasto
    // (RelazioneGiornalieraService.applicaBuonoPastoAuto) e del PDF di riepilogo.
    const oreInvestigativeApprovate = useMemo(() => relazioniDelMese
        .filter(r => r.incaricoId && r.stato === 'APPROVATO')
        .reduce((sum, r) => {
            if (r.oreApprovateTotali != null) return sum + Number(r.oreApprovateTotali);
            return sum + (r.oreGps || 0) + (r.oreExtraApprovate ?? r.oreExtra ?? 0);
        }, 0),
    [relazioniDelMese]);

    // Servizi inseriti: come per le investigazioni, sui report dei turni.
    const oreServiziInserite = useMemo(() => relazioniDelMese
        .filter(r => r.turnoId)
        .reduce((sum, r) => sum + (r.oreGps || 0) + (r.oreExtra || 0), 0),
    [relazioniDelMese]);

    // Servizi approvati: già calcolati dal server (getOreFiduciarieByUserAndPeriod),
    // con override del titolare se presente, altrimenti GPS + extra approvate.
    const oreServiziApprovate = oreFid.oreTotali;

    // Valore unico "Ore Investigative" ancora usato dal PDF di riepilogo.
    const oreInvestigativeMese = oreInvestigativeApprovate || oreInvestigativeInserite;

    // "Inviato" = quanto il collaboratore chiede di rimborso: le spese con
    // anticipatoAzienda=true (carta aziendale) sono già pagate e non si contano.
    //
    // "Approvato" = somma di importoApprovato delle sole spese APPROVATO. Una spesa
    // approvata e poi rettificata torna DA_REVISIONARE e non conta finché non viene
    // riapprovata.
    const totali = useMemo(() => spese.reduce((acc, s) => ({
        inviato: acc.inviato + (s.anticipatoAzienda ? 0 : (s.importoInviato || 0)),
        approvato: acc.approvato + (s.stato === 'APPROVATO' ? (s.importoApprovato || 0) : 0),
        inAttesa: acc.inAttesa + (s.stato === 'IN_ATTESA' || s.stato === 'DA_REVISIONARE' ? 1 : 0),
    }), { inviato: 0, approvato: 0, inAttesa: 0 }), [spese]);

    // Rettifica spesa

    const handleOpenEdit = (spesa: any) => setEditSpesa({ ...spesa, newFile: null, anticipatoAzienda: !!spesa.anticipatoAzienda, autoAziendale: !!spesa.autoAziendale, kmPercorsi: spesa.kmPercorsi ?? '' });

    const handleUpdateSpesa = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        const fd = new FormData();
        fd.append('data', JSON.stringify({
            data: editSpesa.data?.split('T')[0] ?? editSpesa.data,
            importoInviato: editSpesa.importoInviato,
            tipo: editSpesa.tipo,
            note: editSpesa.note || '',
            anticipatoAzienda: !!editSpesa.anticipatoAzienda,
            autoAziendale: !!editSpesa.autoAziendale,
            // parseDecimal accetta virgola o punto e rifiuta i negativi.
            km: editSpesa.kmPercorsi ? parseDecimal(editSpesa.kmPercorsi) : null,
            incaricoId: editSpesa.incaricoId ?? null,
            // Manteniamo turnoId nella rettifica di una spesa fiduciaria. Il backend lo
            // ignora se c'è relazioneId (SpesaDettaglio), altrimenti lo riapplica.
            turnoId: editSpesa.turnoId ?? null,
            relazioneId: editSpesa.relazioneId ?? null,
        }));
        if (editSpesa.newFile) fd.append('file', editSpesa.newFile);
        try {
            await SpeseService.update(editSpesa.id, fd);
            addToast('Rettifica inviata in approvazione', 'success');
            setEditSpesa(null);
            loadData();
        } catch { addToast("Errore durante l'invio della rettifica", 'error'); }
        finally { setIsSaving(false); }
    };

    // Nuove spese

    const openNuova = (inc: Incarico, rel?: any) => {
        setRighe([]);
        setNuovaIncarico(inc);
        setNuovaTurno(null);
        setNuovaRelazione(rel || null);
        setModalKm(''); setModalKmImporto(''); setModalKmAuto(false);
        // Precompila la data quando è univoca (report selezionato o incarico di un
        // giorno); in quel caso il campo non viene mostrato.
        setModalKmData(resolveContextDate(inc, null, rel || null) ?? today);
    };

    // Come openNuova ma per i turni fiduciari. Il report del turno non è più
    // modificabile dopo la chiusura, quindi questo è l'unico modo per aggiungere
    // spese e scontrini.
    const openNuovaTurno = (turno: any) => {
        setRighe([]);
        setNuovaIncarico(null);
        setNuovaTurno(turno);
        setNuovaRelazione(null);
        setModalKm(''); setModalKmImporto(''); setModalKmAuto(false);
        setModalKmData(resolveContextDate(null, turno, null) ?? today);
    };
    const addRiga = () => {
        // Data della nuova riga: quella del contesto se univoca, altrimenti oggi e
        // l'utente la corregge.
        const defaultDate = resolveContextDate(nuovaIncarico, nuovaTurno, nuovaRelazione) ?? today;
        setRighe(prev => [...prev, newRow(defaultDate)]);
    };

    // Se la data del contesto è nota, la UI nasconde i campi data (sezione KM e
    // ogni riga spesa).
    const singleDateForContext = useMemo(
        () => resolveContextDate(nuovaIncarico, nuovaTurno, nuovaRelazione),
        [nuovaIncarico, nuovaTurno, nuovaRelazione]
    );
    const removeRiga = (i: number) => setRighe(prev => prev.filter((_, idx) => idx !== i));
    const updateRiga = (i: number, f: keyof SpesaRow, v: any) => setRighe(prev => { const n = [...prev]; (n[i] as any)[f] = v; return n; });

    const handleSubmitNuove = async (e: React.FormEvent) => {
        e.preventDefault();
        // parseDecimal accetta virgola o punto e rifiuta i negativi.
        const kmValidi = parseDecimal(modalKm) > 0;
        if (!kmValidi && righe.length === 0) { addToast('Inserisci almeno i km o una spesa', 'error'); return; }
        for (let i = 0; i < righe.length; i++) {
            const r = righe[i];
            if (!r.tipo) { addToast(`Spesa #${i + 1}: seleziona la categoria`, 'error'); return; }
            if (parseDecimal(r.importo) <= 0) { addToast(`Spesa #${i + 1}: importo non valido`, 'error'); return; }
        }
        setIsSubmitting(true);
        let totalInviati = 0;
        let totalAttachedToReport = 0;
        // Una spesa è legata a un incarico oppure a un turno (entrambi nullable lato
        // backend). Gli id si calcolano qui per non duplicare il codice tra km e voci.
        const parentIds = {
            incaricoId: nuovaIncarico?.id ?? null,
            turnoId: nuovaTurno?.id ?? null,
        };

        // Collegamento al report:
        // 1) aperto da "Aggiungi spesa a questo report" → tutte le spese vanno su `nuovaRelazione`;
        // 2) altrimenti si cerca per data una relazione esistente dell'incarico.
        // Con relazioneId il backend crea SpesaDettaglio e rimette la relazione in DA_REVISIONARE.
        const relazioniIncarico = nuovaIncarico
            ? (relazioniPerIncarico[nuovaIncarico.id] || [])
            : [];
        const dateToRelId: Record<string, number> = {};
        relazioniIncarico.forEach((r: any) => {
            const k = (r.dataRelazione || '').substring(0, 10);
            if (k && dateToRelId[k] == null) dateToRelId[k] = r.id;
        });
        const findRelazioneIdFor = (data: string): number | null => {
            if (nuovaRelazione?.id != null) return nuovaRelazione.id;
            if (!data) return null;
            const key = data.substring(0, 10);
            return dateToRelId[key] ?? null;
        };

        try {
            if (kmValidi) {
                const fd = new FormData();
                const kmData = modalKmData || today;
                const relId = findRelazioneIdFor(kmData);
                if (relId != null) totalAttachedToReport++;
                fd.append('data', JSON.stringify({
                    data: kmData,
                    importoInviato: parseDecimal(modalKmImporto),
                    tipo: 'Km',
                    note: '',
                    anticipatoAzienda: false,
                    autoAziendale: modalKmAuto,
                    km: parseDecimal(modalKm),
                    ...parentIds,
                    relazioneId: relId,
                }));
                await SpeseService.create(fd);
                totalInviati++;
            }
            for (const r of righe) {
                const fd = new FormData();
                const relId = findRelazioneIdFor(r.data);
                if (relId != null) totalAttachedToReport++;
                fd.append('data', JSON.stringify({
                    data: r.data,
                    importoInviato: parseDecimal(r.importo),
                    tipo: r.tipo,
                    note: r.note,
                    anticipatoAzienda: r.anticipatoAzienda,
                    autoAziendale: r.autoAziendale,
                    km: r.kmPercorsi ? parseDecimal(r.kmPercorsi) : null,
                    ...parentIds,
                    relazioneId: relId,
                }));
                if (r.file) fd.append('file', r.file);
                await SpeseService.create(fd);
                totalInviati++;
            }
            const baseMsg = `${totalInviati} voce/i inviata/e in approvazione`;
            const reportMsg = totalAttachedToReport > 0
                ? ` · ${totalAttachedToReport} allegata/e al report del giorno (torna in revisione)`
                : '';
            addToast(baseMsg + reportMsg, 'success');
            setNuovaIncarico(null);
            setNuovaTurno(null);
            setNuovaRelazione(null);
            await loadData();
        } catch (e: any) {
            // Le voci già inviate restano: si ricarica per mostrarle, con il motivo del rifiuto.
            addToast(e?.response?.data?.message || "Errore durante l'invio", 'error');
            if (totalInviati > 0) loadData();
        }
        finally { setIsSubmitting(false); }
    };

    const prevMonth = () => { if (mese === 1) { setMese(12); setAnno(anno - 1); } else setMese(mese - 1); };
    const nextMonth = () => { if (mese === 12) { setMese(1); setAnno(anno + 1); } else setMese(mese + 1); };
    const meseLabel = new Date(anno, mese - 1).toLocaleString('it-IT', { month: 'long', year: 'numeric' });

    // Spese raggruppate per data dentro la card: km e scontrini dello stesso giorno
    // arrivano di solito insieme. Data più recente in alto, km prima degli scontrini.
    const groupByDate = (lista: any[]): Array<{ data: string; spese: any[] }> => {
        const map: Record<string, any[]> = {};
        lista.forEach(s => {
            const key = (s.data || '').substring(0, 10);
            (map[key] = map[key] || []).push(s);
        });
        return Object.keys(map)
            .sort((a, b) => b.localeCompare(a))
            .map(data => ({
                data,
                spese: map[data].sort((a, b) => {
                    const ak = isKmType(a.tipo) ? 0 : 1;
                    const bk = isKmType(b.tipo) ? 0 : 1;
                    return ak - bk;
                }),
            }));
    };

    const SpeseRaggruppate: React.FC<{ lista: any[]; emptyText: string; readOnly?: boolean }> = ({ lista, emptyText, readOnly = false }) => {
        if (lista.length === 0) {
            return <p className="text-sm text-slate-400 italic text-center py-2">{emptyText}</p>;
        }
        const gruppi = groupByDate(lista);
        return (
            <div className="space-y-3">
                {gruppi.map(({ data, spese: speseGiorno }) => {
                    const totGiorno = speseGiorno.reduce((s, sp) => s + (Number(sp.importoInviato) || 0), 0);
                    const dataFmt = new Date(data).toLocaleDateString('it-IT', {
                        weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
                    });
                    return (
                        <div key={data} className="rounded-2xl border border-slate-200 bg-slate-50/40 overflow-hidden">
                            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white border-b border-slate-200">
                                <div className="flex items-center gap-2 text-xs font-black text-slate-700 uppercase tracking-wide">
                                    <Calendar size={12} className="text-blue-500" />
                                    {dataFmt}
                                    <span className="text-[10px] font-bold text-slate-400 normal-case tracking-normal ml-1">
                                        ({speseGiorno.length} {speseGiorno.length === 1 ? 'voce' : 'voci'})
                                    </span>
                                </div>
                                <span className="text-[11px] font-bold text-slate-500">€ {totGiorno.toFixed(2)}</span>
                            </div>
                            <div className="p-2 space-y-2">
                                {speseGiorno.map((s: any) => <SpesaRowCard key={chiaveSpesa(s)} spesa={s} readOnly={readOnly} />)}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Report inviati in sola lettura (orari, ore, km, auto), senza il testo della
    // relazione. "Rettifica" apre la modale.
    const ReportInviatoCard: React.FC<{ rel: any; timbrature?: string }> = ({ rel, timbrature }) => {
        const stato = rel.stato || 'IN_ATTESA';
        const isDaModificare = stato === 'DA_MODIFICARE';
        const isApprovato = stato === 'APPROVATO';
        const isRifiutato = stato === 'RIFIUTATO';
        const badgeClass = isDaModificare
            ? 'bg-amber-100 text-amber-700 border-amber-200'
            : isApprovato
                ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                : isRifiutato
                    ? 'bg-rose-100 text-rose-700 border-rose-200'
                    : 'bg-blue-100 text-blue-700 border-blue-200';
        const dataFmt = rel.dataRelazione
            ? new Date(rel.dataRelazione).toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' })
            : '—';
        return (
            <div className="border border-slate-200 rounded-2xl p-3 space-y-2 bg-slate-50/40">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                        <FileText size={13} className="text-blue-500" />
                        <span>Report {dataFmt}</span>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider ${badgeClass}`}>
                            {stato.replace('_', ' ')}
                        </span>
                        <button
                            onClick={() => openEditReport(rel)}
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 transition-colors"
                            title="Modifica orari, ore extra, km, auto e re-invia in approvazione"
                        >
                            <Edit2 size={11} /> Modifica
                        </button>
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px] bg-white border border-slate-100 rounded-lg p-2">
                    {/* Timbrature start/stop effettive, anche multiple, sia per incarichi sia per turni. */}
                    <div className="col-span-2">
                        <span className="block text-[9px] font-bold text-slate-400 uppercase">Timbrature</span>
                        <span className="font-semibold text-slate-800">{timbrature || '—'}</span>
                    </div>
                    {/*
                      "Orario Effettivo Servizio Investigativo" non vale per i turni, che hanno orario pianificato
                      e sessioni GPS: nascosto se c'è rel.turnoId.
                    */}
                    {!rel.turnoId && (
                        <div>
                            <span className="block text-[9px] font-bold text-slate-400 uppercase">Orario indagine</span>
                            <span className="font-semibold text-slate-800">
                                {rel.orarioInizioIndagine || '—'} – {rel.orarioFineIndagine || '—'}
                            </span>
                        </div>
                    )}
                    {/*
                      Come nella modale Agenda Collaboratore mostriamo direttamente ore e km approvati.
                      Se il report non è ancora APPROVATO mostriamo i valori richiesti in grigio
                      con "(in attesa)".
                    */}
                    {(() => {
                        const approvato = rel.stato === 'APPROVATO';
                        const oreRichieste = Number(rel.oreGps || 0) + Number(rel.oreExtra || 0);
                        // Per i turni l'override approvato sta su Turno.oreCollaboratoreOverride
                        // (turnoOreCollaboratoreOverride nel DTO), per gli incarichi su oreApprovateTotali.
                        // Senza override: GPS + ore extra approvate (o dichiarate).
                        const isFiduciario = rel.turnoId != null;
                        const overrideFid = isFiduciario && rel.turnoOreCollaboratoreOverride != null
                            ? Math.max(0, Number(rel.turnoOreCollaboratoreOverride))
                            : null;
                        const overrideInv = !isFiduciario && rel.oreApprovateTotali != null
                            ? Math.max(0, Number(rel.oreApprovateTotali))
                            : null;
                        const oreApprovate = overrideFid ?? overrideInv
                            ?? Number(rel.oreGps || 0) + Math.max(0, Number(rel.oreExtraApprovate ?? rel.oreExtra ?? 0));
                        const oreShow = approvato ? oreApprovate : oreRichieste;
                        const kmShow = approvato ? Number(rel.kmApprovati ?? rel.kmPercorsi ?? 0) : Number(rel.kmPercorsi ?? 0);
                        return (
                            <>
                                <div>
                                    <span className="block text-[9px] font-bold text-slate-400 uppercase">
                                        {approvato ? 'Ore approvate' : 'Ore (in attesa)'}
                                    </span>
                                    <span className={`font-semibold ${approvato ? 'text-emerald-700' : 'text-slate-500'}`}>
                                        {fmtHM(oreShow)}
                                    </span>
                                </div>
                                <div>
                                    <span className="block text-[9px] font-bold text-slate-400 uppercase">
                                        {approvato ? 'Km approvati' : 'Km (in attesa)'}
                                    </span>
                                    <span className={`font-semibold ${approvato ? 'text-emerald-700' : 'text-slate-500'}`}>
                                        {kmShow > 0 ? `${Math.round(kmShow)}` : '—'}
                                    </span>
                                </div>
                            </>
                        );
                    })()}
                    <div className="col-span-2 sm:col-span-1">
                        <span className="block text-[9px] font-bold text-slate-400 uppercase">Auto</span>
                        <span className="font-semibold text-slate-800">{rel.autoAziendale ? 'Aziendale' : 'Privata'}</span>
                    </div>
                </div>
                {rel.motivoRichiestaModifica && isDaModificare && (
                    <div className="flex gap-2 p-2 rounded-lg text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
                        <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                        <span><span className="font-bold">Richiesta titolare:</span> {rel.motivoRichiestaModifica}</span>
                    </div>
                )}
            </div>
        );
    };

    // Componenti riga

    const SpesaRowCard: React.FC<{ spesa: any; embedded?: boolean; readOnly?: boolean }> = ({ spesa, embedded = false, readOnly = false }) => {
        const st = getStatus(spesa.stato);
        const km = isKmType(spesa.tipo);
        // Spesa approvata con importo diverso da quello inviato (di solito ridotto):
        // deve essere evidente al collaboratore.
        const importoInviato = Number(spesa.importoInviato) || 0;
        const importoApprovato = spesa.importoApprovato != null ? Number(spesa.importoApprovato) : null;
        const isApprovato = spesa.stato === 'APPROVATO' && !spesa.anticipatoAzienda;
        const delta = isApprovato && importoApprovato != null ? importoApprovato - importoInviato : 0;
        const tagliata = isApprovato && delta < -0.005;
        const aumentata = isApprovato && delta > 0.005;
        // Dentro un report (embedded) stato e modifica sono del report: niente badge
        // né matita sulla singola spesa, e bordo neutro.
        const rowClass = embedded
            ? 'border border-slate-200 bg-white rounded-xl p-3 space-y-1.5'
            : `border rounded-2xl p-4 space-y-2 ${tagliata ? 'border-amber-300 bg-amber-50/50' : st.row}`;
        return (
            <div className={rowClass}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-sm font-bold text-slate-700">
                        {km ? <Car size={13} className="text-blue-400" /> : <Calendar size={13} className="text-slate-400" />}
                        {new Date(spesa.data).toLocaleDateString('it-IT')}
                        <span className="text-slate-300">·</span>
                        <span>{spesa.tipo}</span>
                        {km && spesa.kmPercorsi > 0 && <span className="flex items-center gap-1 text-[10px] font-black bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 rounded-full uppercase"><Car size={9} /> {spesa.kmPercorsi} km</span>}
                        {spesa.anticipatoAzienda && <span className="text-[9px] bg-purple-100 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded-full font-black uppercase">Carta Az.</span>}
                        {km && spesa.autoAziendale && <span className="text-[9px] bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded-full font-black uppercase">Auto Az.</span>}
                    </div>
                    {!embedded && (
                        <div className="flex items-center gap-2 ml-auto">
                            {tagliata && (
                                <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider bg-amber-100 text-amber-800 border-amber-300">
                                    <AlertTriangle size={11} /> Rettificata
                                </span>
                            )}
                            {aumentata && (
                                <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider bg-emerald-100 text-emerald-800 border-emerald-300">
                                    <CheckCircle size={11} /> Aumentata
                                </span>
                            )}
                            <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider ${st.badge}`}>{st.icon} {st.label}</span>
                            {/*
                              Buono pasto automatico: generato in approvazione, non modificabile dal
                              collaboratore (solo badge informativo).
                            */}
                            {spesa.buonoPastoAuto ? (
                                <span className="text-[9px] font-bold uppercase bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded">
                                    Auto
                                </span>
                            ) : !readOnly && (
                                <button onClick={() => handleOpenEdit(spesa)} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"><Edit2 size={14} /></button>
                            )}
                        </div>
                    )}
                    {/* In embedded badge e matita sono nascosti, ma la rettifica va segnalata comunque. */}
                    {embedded && (tagliata || aumentata) && (
                        <span className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider ml-auto ${tagliata ? 'bg-amber-100 text-amber-800 border-amber-300' : 'bg-emerald-100 text-emerald-800 border-emerald-300'}`}>
                            {tagliata ? <AlertTriangle size={11} /> : <CheckCircle size={11} />}
                            {tagliata ? 'Rettificata' : 'Aumentata'}
                        </span>
                    )}
                </div>
                <div className="flex items-end justify-between gap-4">
                    <div className="text-xs text-slate-500">{spesa.note && <span className="italic">{spesa.note}</span>}</div>
                    <div className="flex items-baseline gap-3 shrink-0">
                        <div className="text-right">
                            <span className="text-[10px] text-slate-400 block">{embedded ? 'Richiesto' : 'Richiesto'}</span>
                            <span className={`font-bold text-sm ${tagliata ? 'text-slate-500 line-through' : 'text-slate-600'}`}>€ {importoInviato.toFixed(2)}</span>
                        </div>
                        {/*
                          Importo approvato: sempre per le spese fuori da un report; dentro un report
                          solo se diverso dall'inviato.
                        */}
                        {isApprovato && (!embedded || tagliata || aumentata) && (
                            <div className="text-right">
                                <span className={`text-[10px] block ${tagliata ? 'text-amber-600' : 'text-emerald-500'}`}>
                                    {tagliata ? 'Approvato (rettificato)' : 'Approvato'}
                                </span>
                                <span className={`font-black ${tagliata ? 'text-amber-700' : 'text-emerald-600'}`}>
                                    € {importoApprovato?.toFixed(2) ?? '0.00'}
                                </span>
                                {(tagliata || aumentata) && (
                                    <span className={`block text-[10px] font-bold mt-0.5 ${tagliata ? 'text-amber-700' : 'text-emerald-600'}`}>
                                        {delta > 0 ? '+' : ''}€ {delta.toFixed(2)}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {!embedded && spesa.noteAdmin && (
                    <div className={`flex gap-2 p-2.5 rounded-xl text-xs font-medium ${spesa.stato === 'RIFIUTATO' ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                        <AlertTriangle size={12} className="shrink-0 mt-0.5" />{spesa.noteAdmin}
                    </div>
                )}
            </div>
        );
    };

    // Render

    return (
        <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-5 pb-16">
            {/*
              Titolo + pulsante "Genera PDF di Riepilogo", grande perché molti
              collaboratori usano lo smartphone.
            */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <h1 className="text-2xl font-bold text-slate-900">Le mie Spese e KM</h1>
                <button
                    type="button"
                    onClick={handleDownloadRiepilogo}
                    disabled={pdfLoading || loading}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold shadow-sm transition-colors"
                    title="Scarica il PDF di riepilogo del mese selezionato"
                >
                    {pdfLoading ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
                    Genera PDF di Riepilogo
                </button>
            </div>

            {/* Selettore mese */}
            <div className="flex items-center justify-between bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
                <button onClick={prevMonth} className="p-2 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"><ChevronLeft size={20} /></button>
                <div className="font-bold text-base text-slate-800 capitalize">{meseLabel}</div>
                <button onClick={nextMonth} className="p-2 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"><ChevronRight size={20} /></button>
            </div>

            {/*
              Ore del mese in 4 box: inserite e approvate, per servizi (sinistra) e
              investigazioni (destra), sempre separate.
            */}
            {!loading && (
                <div className="grid grid-cols-2 gap-3">
                    {/* Servizi, inserite dal collaboratore */}
                    <div className="bg-white rounded-2xl border border-indigo-200 p-3 shadow-sm flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
                            <Shield size={16} className="text-indigo-600" />
                        </div>
                        <div className="min-w-0">
                            <span className="text-[10px] font-bold text-slate-400 uppercase block tracking-wider">Ore Servizi da me inseriti</span>
                            <span className="text-lg font-black text-indigo-700">{fmtHM(oreServiziInserite)}</span>
                        </div>
                    </div>

                    {/* Investigazioni, inserite dal collaboratore */}
                    <div className="bg-white rounded-2xl border border-blue-200 p-3 shadow-sm flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                            <Briefcase size={16} className="text-blue-600" />
                        </div>
                        <div className="min-w-0">
                            <span className="text-[10px] font-bold text-slate-400 uppercase block tracking-wider">Ore Investigative da me inserite</span>
                            <span className="text-lg font-black text-blue-700">{fmtHM(oreInvestigativeInserite)}</span>
                        </div>
                    </div>

                    {/* Servizi, approvate dall'azienda */}
                    <div className="bg-white rounded-2xl border border-emerald-200 p-3 shadow-sm flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                            <Shield size={16} className="text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                            <span className="text-[10px] font-bold text-emerald-500 uppercase block tracking-wider">Ore Servizi approvate dall'azienda</span>
                            <span className="text-lg font-black text-emerald-700">{fmtHM(oreServiziApprovate)}</span>
                        </div>
                    </div>

                    {/* Investigazioni, approvate dall'azienda */}
                    <div className="bg-white rounded-2xl border border-emerald-200 p-3 shadow-sm flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                            <Briefcase size={16} className="text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                            <span className="text-[10px] font-bold text-emerald-500 uppercase block tracking-wider">Ore Investigative approvate dall'azienda</span>
                            <span className="text-lg font-black text-emerald-700">{fmtHM(oreInvestigativeApprovate)}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Riepilogo spese (€ inviato/approvato/in attesa) */}
            {!loading && spese.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                    <div className="bg-white rounded-2xl border border-slate-200 p-3 text-center shadow-sm">
                        <span className="text-[10px] font-bold text-slate-400 uppercase block">Inviato</span>
                        <span className="text-lg font-black text-slate-700">€ {totali.inviato.toFixed(2)}</span>
                    </div>
                    <div className="bg-white rounded-2xl border border-emerald-200 p-3 text-center shadow-sm">
                        <span className="text-[10px] font-bold text-emerald-500 uppercase block">Approvato</span>
                        <span className="text-lg font-black text-emerald-600">€ {totali.approvato.toFixed(2)}</span>
                    </div>
                    <div className="bg-white rounded-2xl border border-blue-200 p-3 text-center shadow-sm">
                        <span className="text-[10px] font-bold text-blue-400 uppercase block">In Attesa</span>
                        <span className="text-lg font-black text-blue-600">{totali.inAttesa}</span>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-center py-20"><Loader2 className="animate-spin mx-auto text-blue-500" size={32} /></div>
            ) : incarichiMese.length === 0 && turniMese.length === 0 && speseOrfane.length === 0 && speseDaParentEliminato.length === 0 ? (
                <div className="text-center py-20 text-slate-400">
                    <Receipt size={40} className="mx-auto mb-3 opacity-30" />
                    <p className="font-semibold">Nessun incarico, turno o spesa per {meseLabel}</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {incarichiMese.map(inc => {
                        const incSpese: any[] = spesePerIncarico[inc.id] || [];
                        const incDaModificare: any[] = daModificarePerIncarico[inc.id] || [];
                        const dateRange = inc.dataInizio ? `${fmt(inc.dataInizio)}${inc.dataFine && inc.dataFine !== inc.dataInizio ? ` – ${fmt(inc.dataFine)}` : ''}` : '';

                        return (
                            <div key={inc.id} className={`bg-white border rounded-3xl overflow-hidden shadow-sm ${incDaModificare.length > 0 ? 'border-amber-300 ring-2 ring-amber-100' : 'border-indigo-200'}`}>
                                {/* Header incarico in indigo (investigazioni); i turni fiduciari usano il verde. */}
                                <div className="bg-indigo-50/60 border-b border-indigo-200 px-5 py-4">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <div className="flex items-center gap-2">
                                            <Folder size={16} className="text-indigo-600 shrink-0" />
                                            <span className="font-bold text-slate-800">{praticaName(inc)}</span>
                                            <span className="text-[10px] font-black uppercase tracking-wider bg-indigo-100 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-full">Investigazione</span>
                                            {/* Badge "report da rifare" se almeno una relazione dell'incarico è DA_MODIFICARE. */}
                                            {incDaModificare.length > 0 && (
                                                <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 rounded-full">
                                                    {incDaModificare.length === 1
                                                        ? '1 report da rifare'
                                                        : `${incDaModificare.length} report da rifare`}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-slate-500">
                                            {dateRange && <span className="flex items-center gap-1"><Calendar size={12} /> {dateRange}</span>}
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4 space-y-4">
                                    {/*
                                      Per ogni report inviato: header e spese collegate. Le spese di un report si
                                      aggiungono dal pulsante "Modifica" del report.
                                    */}
                                    {[...(relazioniPerIncarico[inc.id] || [])]
                                        .sort((a, b) => (b.dataRelazione || '').localeCompare(a.dataRelazione || ''))
                                        .map((rel: any) => {
                                            const speseDelReport = incSpese.filter(s => s.relazioneId === rel.id);
                                            return (
                                                <div key={rel.id} className="rounded-2xl border border-slate-200 bg-white p-3 space-y-3">
                                                    <ReportInviatoCard rel={rel} timbrature={formatTimbrature(inc.sessioni, rel.dataRelazione)} />
                                                    {speseDelReport.length > 0 ? (
                                                        <div className="space-y-2 pl-1">
                                                            {speseDelReport
                                                                .sort((a, b) => {
                                                                    const ak = isKmType(a.tipo) ? 0 : 1;
                                                                    const bk = isKmType(b.tipo) ? 0 : 1;
                                                                    return ak - bk;
                                                                })
                                                                .map((s: any) => <SpesaRowCard key={chiaveSpesa(s)} spesa={s} embedded />)}
                                                        </div>
                                                    ) : (
                                                        <p className="text-[11px] text-slate-400 italic text-center py-1">
                                                            Nessuna spesa allegata a questo report.
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })}

                                    {/*
                                      Spese dell'incarico senza report (relazioneId null), es. km o scontrini di
                                      una giornata il cui report non è ancora stato inviato.
                                    */}
                                    {incSpese.some(s => s.relazioneId == null) && (
                                        <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-3 space-y-2">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                                <Receipt size={11} /> Spese senza report
                                            </p>
                                            <SpeseRaggruppate
                                                lista={incSpese.filter(s => s.relazioneId == null)}
                                                emptyText=""
                                            />
                                        </div>
                                    )}

                                    {/* Incarico senza report inviati: si possono comunque aggiungere spese standalone. */}
                                    {(relazioniPerIncarico[inc.id] || []).length === 0 && (
                                        <button onClick={() => openNuova(inc)} className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-indigo-300 text-indigo-700 rounded-xl text-xs font-bold hover:border-indigo-500 hover:text-indigo-800 transition-colors">
                                            <Plus size={14} /> Aggiungi Spesa per questo incarico
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {/*
                      Turni fiduciari del mese: stessa UX delle card incarico, con icona Shield,
                      sotto gli incarichi.
                    */}
                    {turniMese.map((t: any) => {
                        const tSpese: any[] = spesePerTurno[t.id] || [];
                        const tNome = t.servizio?.nome || t.servizioNome || `Turno #${t.id}`;
                        const dataRif = t.dataInizio || t.effettivoInizio || t.dataPianificazione;
                        const dateRange = dataRif ? fmt(dataRif) : '';
                        return (
                            <div key={`turno-${t.id}`} className="bg-white border border-emerald-200 rounded-3xl overflow-hidden shadow-sm">
                                <div className="bg-emerald-50/60 border-b border-emerald-200 px-5 py-4">
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <div className="flex items-center gap-2">
                                            <Shield size={16} className="text-emerald-600 shrink-0" />
                                            <span className="font-bold text-slate-800">{tNome}</span>
                                            <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">Servizio Fiduciario</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-slate-500">
                                            {dateRange && <span className="flex items-center gap-1"><Calendar size={12} /> {dateRange}</span>}
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4 space-y-4">
                                    {/*
                                      Come per gli incarichi: per ogni relazione del turno ReportInviatoCard e spese,
                                      con "Modifica" che apre la rettifica (handleSubmitEditReport propaga già turnoId).
                                    */}
                                    {[...(relazioniPerTurno[t.id] || [])]
                                        .sort((a, b) => (b.dataRelazione || '').localeCompare(a.dataRelazione || ''))
                                        .map((rel: any) => {
                                            const speseDelReport = tSpese.filter(s => s.relazioneId === rel.id);
                                            return (
                                                <div key={rel.id} className="rounded-2xl border border-slate-200 bg-white p-3 space-y-3">
                                                    <ReportInviatoCard rel={rel} timbrature={formatTimbrature(t.sessioni)} />
                                                    {speseDelReport.length > 0 ? (
                                                        <div className="space-y-2 pl-1">
                                                            {speseDelReport
                                                                .sort((a, b) => {
                                                                    const ak = isKmType(a.tipo) ? 0 : 1;
                                                                    const bk = isKmType(b.tipo) ? 0 : 1;
                                                                    return ak - bk;
                                                                })
                                                                .map((s: any) => <SpesaRowCard key={chiaveSpesa(s)} spesa={s} embedded />)}
                                                        </div>
                                                    ) : (
                                                        <p className="text-[11px] text-slate-400 italic text-center py-1">
                                                            Nessuna spesa allegata a questo report.
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        })}

                                    {/* Spese del turno senza relazione, es. scontrini inseriti prima del report di fine turno. */}
                                    {tSpese.some(s => s.relazioneId == null) && (
                                        <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-3 space-y-2">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                                                <Receipt size={11} /> Spese senza report
                                            </p>
                                            <SpeseRaggruppate
                                                lista={tSpese.filter(s => s.relazioneId == null)}
                                                emptyText=""
                                            />
                                        </div>
                                    )}

                                    {/*
                                      "Aggiungi Spesa" solo se il turno non ha ancora relazioni; dopo si passa da
                                      "Modifica" sul report.
                                    */}
                                    {(relazioniPerTurno[t.id] || []).length === 0 && (
                                        <button
                                            onClick={() => openNuovaTurno(t)}
                                            className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-emerald-300 text-emerald-700 rounded-xl text-xs font-bold hover:border-emerald-500 hover:text-emerald-800 transition-colors"
                                        >
                                            <Plus size={14} /> Aggiungi Spesa per questo turno
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {speseOrfane.length > 0 && (
                        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
                            <div className="bg-slate-50 border-b border-slate-200 px-5 py-4">
                                <span className="font-bold text-slate-600 text-sm">Spese non collegate a un incarico</span>
                            </div>
                            <div className="p-4 space-y-3">
                                <SpeseRaggruppate lista={speseOrfane} emptyText="Nessuna spesa orfana." />
                            </div>
                        </div>
                    )}

                    {speseDaParentEliminato.length > 0 && (
                        <div className="bg-white border border-slate-300 rounded-3xl overflow-hidden shadow-sm">
                            <div className="bg-slate-100 border-b border-slate-200 px-5 py-4 flex items-center gap-2">
                                <AlertTriangle size={14} className="text-slate-500" />
                                <span className="font-bold text-slate-700 text-sm">Spese di mandati o turni eliminati</span>
                                <span className="text-[10px] text-slate-500 italic ml-1">solo lettura — il parent non esiste più</span>
                            </div>
                            <div className="p-4 space-y-3">
                                <SpeseRaggruppate lista={speseDaParentEliminato} emptyText="" readOnly />
                            </div>
                        </div>
                    )}
                </div>
            )}


            {/*
              Modale nuova spesa, per incarichi e turni: il submit passa incaricoId o
              turnoId a seconda di quale è valorizzato, e il titolo cambia di conseguenza.
            */}
            {(nuovaIncarico || nuovaTurno) && (
                <Modal
                    title={nuovaRelazione
                        ? `Spese · Report del ${new Date(nuovaRelazione.dataRelazione).toLocaleDateString('it-IT')}`
                        : nuovaIncarico
                            ? `Spese · ${praticaName(nuovaIncarico)}`
                            : `Spese · ${(nuovaTurno as any)?.servizio?.nome || (nuovaTurno as any)?.servizioNome || `Turno #${(nuovaTurno as any)?.id}`} (Servizio Fiduciario)`}
                    onClose={() => { setNuovaIncarico(null); setNuovaTurno(null); setNuovaRelazione(null); }}
                >
                    <form onSubmit={handleSubmitNuove} className="space-y-4">
                        {nuovaRelazione ? (
                            <div className="bg-amber-50 border border-amber-300 p-3 rounded-xl text-xs text-amber-900 font-medium flex gap-2 items-start">
                                <FileText size={14} className="shrink-0 mt-0.5 text-amber-600" />
                                <div>
                                    Stai aggiungendo spese al <strong>report del {new Date(nuovaRelazione.dataRelazione).toLocaleDateString('it-IT')}</strong>.
                                    Il report tornerà in revisione amministrativa.
                                </div>
                            </div>
                        ) : (
                            <div className="bg-blue-50 border border-blue-200 p-3 rounded-xl text-xs text-blue-800 font-medium flex gap-2 items-start">
                                <Receipt size={14} className="shrink-0 mt-0.5 text-blue-500" />
                                <div>
                                    Le spese con data che coincide con un report già inviato verranno
                                    <strong> allegate a quel report</strong> e lo riporteranno in revisione admin.
                                    Altrimenti restano spese singole da approvare.
                                </div>
                            </div>
                        )}

                        {/* Banner data univoca: se il lavoro o il report è di un giorno non chiediamo la data. */}
                        {singleDateForContext && (
                            <div className="bg-slate-50 border border-slate-200 px-3 py-2 rounded-xl text-[11px] text-slate-700 font-medium flex items-center gap-1.5">
                                <Calendar size={12} className="text-slate-500" />
                                Data spese: <strong>{new Date(singleDateForContext).toLocaleDateString('it-IT')}</strong>
                                <span className="text-slate-400"></span>
                            </div>
                        )}

                        {/* KM */}
                        <div className="space-y-2">
                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Car size={12} /> Chilometri</h3>
                            <div className={singleDateForContext ? '' : 'grid grid-cols-2 gap-3'}>
                                {!singleDateForContext && (
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Data</label>
                                        <input type="date" className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold outline-none focus:ring-2 ring-blue-500"
                                            value={modalKmData} onChange={e => setModalKmData(e.target.value)} />
                                    </div>
                                )}
                                <div className="bg-white border border-slate-200 p-3 rounded-2xl shadow-sm focus-within:ring-2 ring-blue-100">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1">Km Percorsi</label>
                                    <NumberInput
                                           min={0} className="w-full bg-transparent font-semibold text-lg text-slate-800 outline-none"
                                        value={modalKm} onChange={e => setModalKm(e.target.value)} placeholder="0" />
                                    <AutoAziendaleSelector className="mt-2" value={modalKmAuto} onChange={setModalKmAuto} />
                                </div>
                            </div>
                            {parseDecimal(modalKm) > 0 && (
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Importo Rimborso KM (€)</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2.5 text-slate-400 font-bold text-sm">€</span>
                                        <NumberInput
                                               min={0} step="0.01" className="w-full p-2.5 pl-7 bg-white border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 ring-blue-500"
                                            value={modalKmImporto} onChange={e => setModalKmImporto(e.target.value)} placeholder="0.00" />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Spese */}
                        <div className="flex items-center justify-between px-1">
                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Receipt size={12} /> Spese</h3>
                        </div>
                        <div className="space-y-4 max-h-[40vh] overflow-y-auto pr-1">
                            {righe.map((r, i) => (
                                <div key={i} className="relative bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3 group">
                                    {righe.length > 1 && (
                                        <button type="button" onClick={() => removeRiga(i)} className="absolute -top-2 -right-2 w-6 h-6 bg-white border border-slate-200 text-slate-400 hover:text-rose-500 rounded-full flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>
                                    )}
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Spesa #{i + 1}</p>
                                    {singleDateForContext ? (
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Categoria *</label>
                                            <select required className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold outline-none focus:ring-2 ring-blue-500" value={r.tipo} onChange={e => updateRiga(i, 'tipo', e.target.value)}>
                                                <option value="">-- Seleziona --</option>
                                                {TIPI_SPESA.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Data *</label>
                                                <input type="date" required className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold outline-none focus:ring-2 ring-blue-500" value={r.data} onChange={e => updateRiga(i, 'data', e.target.value)} />
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Categoria *</label>
                                                <select required className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold outline-none focus:ring-2 ring-blue-500" value={r.tipo} onChange={e => updateRiga(i, 'tipo', e.target.value)}>
                                                    <option value="">-- Seleziona --</option>
                                                    {TIPI_SPESA.map(t => <option key={t} value={t}>{t}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Importo (€) *</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-2.5 text-slate-400 font-bold text-sm">€</span>
                                            <NumberInput
                                                   min={0} step="0.01" required className="w-full p-2.5 pl-7 bg-white border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 ring-blue-500" value={r.importo} onChange={e => updateRiga(i, 'importo', e.target.value)} placeholder="0.00" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Note</label>
                                        <input type="text" className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 ring-blue-500" placeholder="Es. Pranzo, Parcheggio..." value={r.note} onChange={e => updateRiga(i, 'note', e.target.value)} />
                                    </div>
                                    <Toggle checked={r.anticipatoAzienda} onChange={() => updateRiga(i, 'anticipatoAzienda', !r.anticipatoAzienda)}
                                        label="Carta Aziendale" sub="Nessun rimborso richiesto" activeClass="bg-purple-50 border-purple-300"
                                        icon={<Receipt size={16} className={r.anticipatoAzienda ? 'text-purple-400' : 'text-slate-300'} />} />
                                    <label className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-xl cursor-pointer transition-all text-sm font-bold uppercase ${r.file ? 'bg-emerald-50 border-emerald-300 text-emerald-600' : 'bg-white border-slate-200 text-slate-400 hover:bg-slate-50'}`}>
                                        <Camera size={15} />{r.file ? r.file.name : 'Allega Scontrino'}
                                        <input type="file" capture="environment" accept="image/*" className="hidden" onChange={e => { if (e.target.files) updateRiga(i, 'file', e.target.files[0]); }} />
                                    </label>
                                </div>
                            ))}
                        </div>
                        <button type="button" onClick={addRiga} className="w-full py-2.5 border-2 border-dashed border-slate-300 text-slate-500 rounded-xl text-sm font-bold hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2">
                            <Plus size={15} /> Aggiungi altra spesa
                        </button>
                        <button type="submit" disabled={isSubmitting} className="w-full bg-slate-900 text-white py-3.5 rounded-2xl font-bold shadow-lg hover:bg-slate-800 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                            {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                            {isSubmitting ? 'Invio...' : `Invia in Approvazione`}
                        </button>
                    </form>
                </Modal>
            )}

            {/*
              Modale rettifica report, con lo stesso layout di "Gestione Tempi" in ReportTab:
              - banner sulla rettifica che rimanda in approvazione
              - orario effettivo servizio investigativo
              - ore registrate (sola lettura, da GPS), ore extra, km e auto
              Il testo della relazione lo gestisce solo il titolare.
            */}
            {editReport && (
                <Modal title="Rettifica Report" onClose={() => setEditReport(null)}>
                    <form onSubmit={handleSubmitEditReport} className="space-y-5">
                        <div className="bg-amber-50 border-2 border-amber-300 text-amber-900 p-4 rounded-2xl flex gap-3 text-sm font-medium shadow-sm">
                            <AlertTriangle size={22} className="shrink-0 text-amber-600 mt-0.5" />
                            <div className="min-w-0">
                                <p className="font-black uppercase tracking-wider text-xs mb-1">Rettifica e re-invio</p>
                                <p className="text-[11px] text-amber-700/90 leading-relaxed">
                                    Il report tornerà in revisione amministrativa. Il testo della relazione
                                    e le spese già allegate restano invariati.
                                </p>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 px-1">Gestione Tempi</h3>

                            {/*
                              Orari indagine solo per gli incarichi investigativi; per i turni contano
                              l'orario pianificato e le sessioni GPS, quindi il campo è nascosto.
                            */}
                            {!editReport.turnoId && (
                                <div className="bg-blue-50 border border-blue-200 p-4 sm:p-5 rounded-2xl mb-4 shadow-sm">
                                    <label className="text-[10px] font-bold text-blue-800 uppercase tracking-wide block mb-3 flex items-center gap-1.5">
                                        <Clock size={14} className="text-blue-600" /> Orario Effettivo Servizio Investigativo
                                    </label>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide block mb-1">Inizio Sul Posto</label>
                                            <input type="time" className="w-full bg-white border border-slate-200 p-2.5 rounded-lg font-semibold text-slate-800 outline-none focus:ring-2 ring-blue-500"
                                                   value={reportForm.orarioInizio}
                                                   onChange={e => setReportForm({ ...reportForm, orarioInizio: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide block mb-1">Fine Sul Posto</label>
                                            <input type="time" className="w-full bg-white border border-slate-200 p-2.5 rounded-lg font-semibold text-slate-800 outline-none focus:ring-2 ring-blue-500"
                                                   value={reportForm.orarioFine}
                                                   onChange={e => setReportForm({ ...reportForm, orarioFine: e.target.value })} />
                                        </div>
                                    </div>
                                    <p className="text-[9px] text-blue-600/80 mt-2 font-medium leading-tight">
                                        * Inserisci l'orario in cui è iniziata e finita l'indagine sul target.
                                    </p>
                                </div>
                            )}

                            {/* Ore registrate (sola lettura), extra e km, come in ReportTab */}
                            <div className="grid grid-cols-3 gap-3">
                                <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-2xl flex flex-col justify-center shadow-sm">
                                    <label className="text-[10px] font-bold text-emerald-600/70 uppercase tracking-wide mb-1 flex items-center gap-1">
                                        <Activity size={12} /> Ore Registrate
                                    </label>
                                    <div className="text-2xl font-black text-emerald-700">
                                        {editReport.oreGps ?? 0}<span className="text-sm font-semibold text-emerald-600/50">h</span>
                                    </div>
                                </div>
                                <div className="bg-white border border-slate-200 p-3 rounded-2xl shadow-sm focus-within:ring-2 ring-blue-100">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1">Ore Extra (Ufficio)</label>
                                    <NumberInput min={0} step="0.5"
                                           className="w-full bg-transparent font-semibold text-lg text-slate-800 outline-none"
                                           value={reportForm.oreExtra}
                                           onChange={e => setReportForm({ ...reportForm, oreExtra: e.target.value })}
                                           placeholder="0.0" />
                                </div>
                                <div className="bg-white border border-slate-200 p-3 rounded-2xl shadow-sm focus-within:ring-2 ring-blue-100">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1">Km Percorsi</label>
                                    <NumberInput min={0}
                                           className="w-full bg-transparent font-semibold text-lg text-slate-800 outline-none"
                                           value={reportForm.km}
                                           onChange={e => setReportForm({ ...reportForm, km: e.target.value })}
                                           placeholder="0" />
                                    {parseDecimal(reportForm.km) > 0 && (
                                        <AutoAziendaleSelector verticale className="mt-2" value={!!reportForm.autoAziendale}
                                            onChange={v => setReportForm({ ...reportForm, autoAziendale: v })} />
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Spese già allegate al report (per relazioneId), con X per eliminarle subito. */}
                        {(() => {
                            const speseDelReport = spese.filter(s => s.relazioneId === editReport.id);
                            if (speseDelReport.length === 0) return null;
                            return (
                                <div>
                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 px-1 flex items-center gap-1.5">
                                        <Receipt size={12} /> Spese del report
                                    </h3>
                                    <div className="space-y-2">
                                        {speseDelReport.map((s: any) => {
                                            const km = isKmType(s.tipo);
                                            return (
                                                <div key={chiaveSpesa(s)} className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2">
                                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                                        {km ? <Car size={13} className="text-blue-400 shrink-0" /> : <Calendar size={13} className="text-slate-400 shrink-0" />}
                                                        <span className="text-xs font-bold text-slate-700">{s.tipo}</span>
                                                        {km && s.kmPercorsi > 0 && (
                                                            <span className="text-[10px] font-black bg-blue-50 text-blue-700 border border-blue-100 px-1.5 py-0.5 rounded-full uppercase">{s.kmPercorsi} km</span>
                                                        )}
                                                        {s.note && <span className="text-[11px] text-slate-500 italic truncate">{s.note}</span>}
                                                    </div>
                                                    <span className="text-xs font-bold text-slate-600 shrink-0">€ {(s.importoInviato || 0).toFixed(2)}</span>
                                                    {/* Buono pasto automatico: il collaboratore non può eliminarlo. */}
                                                    {s.buonoPastoAuto ? (
                                                        <span className="text-[9px] font-bold uppercase bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 rounded shrink-0">
                                                            Auto
                                                        </span>
                                                    ) : (
                                                        <button type="button" onClick={() => handleDeleteSpesaFromReport(s)}
                                                                className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors shrink-0"
                                                                title="Elimina questa spesa dal report">
                                                            <X size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Nuove spese per questo report */}
                        <div>
                            <div className="flex items-center justify-between px-1 mb-3">
                                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <Plus size={12} /> Aggiungi spese al report
                                </h3>
                                <button type="button" onClick={addRigaEdit}
                                        className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-blue-100 transition-colors">
                                    <Plus size={13} /> Nuova
                                </button>
                            </div>
                            {nuoveSpeseEdit.length === 0 && (
                                <p className="text-[11px] text-slate-400 italic text-center py-2 border border-dashed border-slate-200 rounded-xl">
                                    Nessuna nuova spesa. Clicca "Nuova" per aggiungerne una.
                                </p>
                            )}
                            <div className="space-y-3">
                                {nuoveSpeseEdit.map((r, i) => (
                                    <div key={i} className="relative bg-slate-50 border border-slate-200 rounded-2xl p-3 space-y-2 group">
                                        <button type="button" onClick={() => removeRigaEdit(i)}
                                                className="absolute -top-2 -right-2 w-6 h-6 bg-white border border-slate-200 text-slate-400 hover:text-rose-500 rounded-full flex items-center justify-center shadow-sm">
                                            <X size={12} />
                                        </button>
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Nuova spesa #{i + 1}</p>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Categoria *</label>
                                                <select required className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs font-semibold outline-none focus:ring-2 ring-blue-500"
                                                        value={r.tipo} onChange={e => updateRigaEdit(i, 'tipo', e.target.value)}>
                                                    <option value="">--</option>
                                                    {TIPI_SPESA.map(t => <option key={t} value={t}>{t}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Importo (€) *</label>
                                                <div className="relative">
                                                    <span className="absolute left-2.5 top-2 text-slate-400 font-bold text-xs">€</span>
                                                    <NumberInput min={0} step="0.01" required
                                                           className="w-full p-2 pl-6 bg-white border border-slate-200 rounded-lg text-xs font-bold outline-none focus:ring-2 ring-blue-500"
                                                           value={r.importo} onChange={e => updateRigaEdit(i, 'importo', e.target.value)} placeholder="0.00" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <label className="flex items-center gap-1.5 cursor-pointer select-none">
                                                <input type="checkbox" checked={r.anticipatoAzienda}
                                                       onChange={e => updateRigaEdit(i, 'anticipatoAzienda', e.target.checked)}
                                                       className="w-3.5 h-3.5 rounded text-purple-600 cursor-pointer" />
                                                <span className="text-[9px] font-black text-slate-500 uppercase">Carta Az.</span>
                                            </label>
                                            <label className={`ml-auto flex items-center gap-1 px-2 py-1 border rounded-lg cursor-pointer transition-all text-[10px] font-bold uppercase ${r.file ? 'bg-emerald-50 border-emerald-300 text-emerald-600' : 'bg-white border-slate-200 text-slate-400'}`}>
                                                <Camera size={12} />{r.file ? r.file.name.substring(0, 12) : 'Scontrino'}
                                                <input type="file" capture="environment" accept="image/*" className="hidden"
                                                       onChange={e => { if (e.target.files) updateRigaEdit(i, 'file', e.target.files[0]); }} />
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button type="submit" disabled={isSavingReport}
                                className="w-full bg-amber-600 hover:bg-amber-700 text-white py-4 rounded-xl font-semibold text-lg shadow-lg transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                            {isSavingReport ? <Loader2 className="animate-spin" size={20} /> : <RotateCcw size={20} />}
                            {isSavingReport ? 'Re-invio in corso…' : 'Re-invia Report in Approvazione'}
                        </button>
                    </form>
                </Modal>
            )}

            {/* Modale rettifica spesa */}
            {editSpesa && (
                <Modal title="Rettifica Spesa" onClose={() => setEditSpesa(null)}>
                    <form onSubmit={handleUpdateSpesa} className="space-y-4">
                        <div className="bg-amber-50 p-3 rounded-xl border border-amber-200 flex gap-2 items-start">
                            <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={16} />
                            <p className="text-xs text-amber-800 font-medium">Questa modifica annullerà l'eventuale approvazione e richiederà una nuova verifica.</p>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5">Data</label>
                            <input type="date" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold outline-none focus:ring-2 ring-blue-500"
                                value={editSpesa.data?.split('T')[0] ?? editSpesa.data}
                                onChange={e => setEditSpesa({ ...editSpesa, data: e.target.value })} />
                        </div>
                        {isKmType(editSpesa.tipo) ? (
                            <>
                                <div className="bg-white border border-slate-200 p-3 rounded-2xl shadow-sm focus-within:ring-2 ring-blue-100">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1">Km Percorsi</label>
                                    <NumberInput
                                           min={0} className="w-full bg-transparent font-semibold text-lg text-slate-800 outline-none"
                                        value={editSpesa.kmPercorsi} onChange={e => setEditSpesa({ ...editSpesa, kmPercorsi: e.target.value })} placeholder="0" />
                                    <AutoAziendaleSelector className="mt-2" value={!!editSpesa.autoAziendale}
                                        onChange={v => setEditSpesa({ ...editSpesa, autoAziendale: v })} />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5">Importo Rimborso KM (€)</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-3 text-slate-400 font-bold text-sm">€</span>
                                        <NumberInput
                                               min={0} step="0.01" className="w-full p-3 pl-8 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 ring-blue-500"
                                            value={editSpesa.importoInviato} onChange={e => setEditSpesa({ ...editSpesa, importoInviato: parseDecimal(e.target.value) })} />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5">Categoria</label>
                                    <select className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold outline-none focus:ring-2 ring-blue-500"
                                        value={editSpesa.tipo} onChange={e => setEditSpesa({ ...editSpesa, tipo: e.target.value })}>
                                        {TIPI_SPESA.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5">Importo Rimborso (€)</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-3 text-slate-400 font-bold text-sm">€</span>
                                        <NumberInput
                                               min={0} step="0.01" required className="w-full p-3 pl-8 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 ring-blue-500"
                                            value={editSpesa.importoInviato} onChange={e => setEditSpesa({ ...editSpesa, importoInviato: parseDecimal(e.target.value) })} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5">Note</label>
                                    <input type="text" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 ring-blue-500"
                                        value={editSpesa.note || ''} onChange={e => setEditSpesa({ ...editSpesa, note: e.target.value })} />
                                </div>
                                <Toggle checked={editSpesa.anticipatoAzienda} onChange={() => setEditSpesa({ ...editSpesa, anticipatoAzienda: !editSpesa.anticipatoAzienda })}
                                    label="Pagato con Carta Aziendale" sub="Nessun rimborso richiesto" activeClass="bg-purple-50 border-purple-300"
                                    icon={<Receipt size={20} className={editSpesa.anticipatoAzienda ? 'text-purple-400' : 'text-slate-300'} />} />
                            </>
                        )}
                        <label className={`flex items-center justify-center gap-3 p-4 border-2 border-dashed rounded-2xl cursor-pointer transition-all ${editSpesa.newFile ? 'bg-emerald-50 border-emerald-300 text-emerald-600' : 'bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100'}`}>
                            <Camera size={18} />
                            <span className="font-bold text-sm uppercase">{editSpesa.newFile ? editSpesa.newFile.name : 'Aggiorna Scontrino / Foto'}</span>
                            <input type="file" capture="environment" accept="image/*" className="hidden" onChange={e => { if (e.target.files) setEditSpesa({ ...editSpesa, newFile: e.target.files[0] }); }} />
                        </label>
                        <button type="submit" disabled={isSaving} className="w-full bg-slate-900 text-white py-3.5 rounded-2xl font-bold shadow-lg hover:bg-slate-800 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                            {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                            {isSaving ? 'Invio...' : 'Invia Rettifica Definitiva'}
                        </button>
                    </form>
                </Modal>
            )}
        </div>
    );
};

export default SpeseCollaboratoreView;
